import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { CustomerName } from "@/services/backend/customers/components/customer-name";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { formatDateTime } from "@/shared/lib/utilities/date";
import { CustomerFilter } from "@/services/backend/customers/components/customer-filter";
import { ServiceObjectFilter } from "@/services/backend/customers/components/service-object-filter";
import { CompanyFilter } from "@/services/backend/company/components/company-filter";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { ReactNode } from "react";
import { AirHandlingUnit } from "../types/air-handling-unit";
import {
  ListRequest,
  useHtzAirHandlingUnitListQuery,
} from "@/services/backend/htz/ahu/api/ahu-api";

export function AhuTable({
  request,
  renderRowLink,
}: {
  request: ListRequest;
  renderRowLink: (ahuId: string, children: ReactNode) => ReactNode;
}) {
  const { data, isLoading, error } = useHtzAirHandlingUnitListQuery(request);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Unternehmen")}</TableHead>
          <TableHead>{t("Kunde")}</TableHead>
          <TableHead>{t("Name")}</TableHead>
          <TableHead>{t("Objekt")}</TableHead>
          <TableHead>{t("Erstellt am")}</TableHead>
          <TableHead>{t("Aktualisiert am")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading && <TableLoadingRow colSpan={6} />}
        {error && <TableRtkErrorRow error={error} colSpan={6} />}
        {data && data.items.length === 0 && <TableEmptyRow colSpan={6} />}
        {data &&
          data.items.map((ahu) => (
            <AhuTableRow key={ahu.id} ahu={ahu} renderRowLink={renderRowLink} />
          ))}
      </TableBody>
    </Table>
  );
}

function AhuTableRow({
  ahu,
  renderRowLink,
}: {
  ahu: AirHandlingUnit;
  renderRowLink: (ahuId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        <CompanyName companyId={ahu.companyId} short />
      </TableCell>
      <TableCell>
        <CustomerName customerId={ahu.customerId} />
      </TableCell>
      <TableCell>
        {renderRowLink(ahu.id, <>{ahuNameOrEmptyNameMsg(ahu.name)}</>)}
      </TableCell>
      <TableCell>
        <ServiceObjectName id={ahu.serviceObjectId} />
      </TableCell>
      <TableCell>{formatDateTime(ahu.createdAt)}</TableCell>
      <TableCell>{formatDateTime(ahu.updatedAt)}</TableCell>
    </TableRow>
  );
}

function ahuNameOrEmptyNameMsg(name: string) {
  if (name.trim().length === 0) return t("-ohne Name-");
  return name;
}

export function AhuTableFilters({
  request,
  onRequestChange,
  hideCompanyFilter,
  hideCustomerFilter,
  hideServiceObjectFilter,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
  hideCompanyFilter?: boolean;
  hideCustomerFilter?: boolean;
  hideServiceObjectFilter?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <SearchFilter
        filter={request.name || { active: false, values: [] }}
        onFilterChange={(name) => onRequestChange({ ...request, name })}
        placeholder={t("Nach Name suchen ...")}
        className="h-8"
      />
      <CompanyFilter
        filter={request.company ?? { active: false, values: [] }}
        onFilterChange={(company) => onRequestChange({ ...request, company })}
        hidden={hideCompanyFilter}
      />
      <CustomerFilter
        filter={request.customer ?? { active: false, values: [] }}
        onFilterChange={(customer) => onRequestChange({ ...request, customer })}
        hidden={hideCustomerFilter}
      />
      <ServiceObjectFilter
        filter={request.serviceObject ?? { active: false, values: [] }}
        onFilterChange={(serviceObject) =>
          onRequestChange({ ...request, serviceObject })
        }
        customerIds={request.customer?.values}
        hidden={hideServiceObjectFilter}
      />
    </div>
  );
}
